import { useEffect, useRef, useState } from "react";
import { apiRequest } from "../../services/api";
export default function FinancialReview({ companyId, yearId, accessToken }) {
  const [asOf, setAsOf] = useState(""),
    [report, setReport] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const generation = useRef(0);
  useEffect(() => {
    generation.current++;
    setReport(null);
    setError("");
    setBusy(false);
    return () => {
      generation.current++;
    };
  }, [companyId, yearId, accessToken]);
  async function load(e) {
    e.preventDefault();
    const g = ++generation.current;
    setBusy(true);
    setError("");
    setReport(null);
    try {
      const r = await apiRequest(
        `/accounting/${companyId}/financial-review?${new URLSearchParams({ yearId, asOf })}`,
        { accessToken },
      );
      if (g === generation.current) setReport(r);
    } catch (err) {
      if (g === generation.current) setError(err.message);
    } finally {
      if (g === generation.current) setBusy(false);
    }
  }
  return (
    <section className="mt-6 space-y-4 rounded-2xl border bg-white p-5">
      <h2 className="text-lg font-semibold">
        Financial review & close readiness
      </h2>
      <p className="text-sm text-slate-600">
        Draft posted-book figures with explicit incomplete reconciliation gates.
        This does not close the year, carry balances forward or certify GST
        filing readiness.
      </p>
      <form className="flex flex-wrap items-end gap-3" onSubmit={load}>
        <label className="text-sm">
          Review through
          <input
            required
            type="date"
            className="mt-1 block rounded-lg border p-2"
            value={asOf}
            onChange={(e) => {
              generation.current++;
              setAsOf(e.target.value);
              setReport(null);
              setBusy(false);
            }}
          />
        </label>
        <button
          disabled={busy || !companyId || !yearId}
          className="rounded-lg border px-4 py-2 disabled:opacity-40"
        >
          Load financial review
        </button>
      </form>
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      {report && (
        <>
          <div className="rounded-lg bg-amber-50 p-3 text-sm">
            {report.warning}
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ["Income", report.income],
              ["Expenses", report.expenses],
              ["Current-year result", report.currentYearResult],
              ["Trial difference", report.trialDifference],
              ["Assets", report.assets],
              ["Liabilities", report.liabilities],
              [
                "Equity including current result",
                report.equityIncludingCurrentResult,
              ],
              ["Position difference", report.positionDifference],
            ].map(([label, value]) => (
              <div className="rounded-lg border p-3" key={label}>
                <p className="text-xs text-slate-500">{label} ₹</p>
                <p className="mt-1 font-semibold tabular-nums">{value}</p>
              </div>
            ))}
          </div>
          <p className="text-sm">
            Draft vouchers: {report.controls.draftVouchers} · Opening entries:{" "}
            {report.controls.postedOpeningVouchers} · Unregistered entries to
            review: {report.controls.unregisteredPostedVouchers} · Active
            evidence credits not posted:{" "}
            {report.controls.unpostedActiveEvidenceCredits} · Manual settlements
            not independently bank-reconciled:{" "}
            {
              report.controls
                .manualSettlementPostingsAwaitingIndependentBankReconciliation
            }
          </p>
          <div className="space-y-2">
            {report.gates.map((g) => (
              <article key={g.code} className="rounded-lg border p-3 text-sm">
                <strong>
                  {g.code.replaceAll("_", " ")} ·{" "}
                  {g.status.replaceAll("_", " ")}
                </strong>
                <p className="mt-1 text-slate-600">{g.detail}</p>
              </article>
            ))}
          </div>
          <details>
            <summary className="cursor-pointer text-sm font-semibold">
              Posted ledger detail — {report.from} to {report.asOf}
            </summary>
            <div className="overflow-auto">
              <table className="mt-3 w-full text-left text-sm">
                <thead>
                  <tr>
                    <th>Ledger</th>
                    <th>Nature</th>
                    <th>Debit ₹</th>
                    <th>Credit ₹</th>
                    <th>Natural balance ₹</th>
                  </tr>
                </thead>
                <tbody>
                  {report.lines.map((l) => (
                    <tr key={l.id} className="border-t">
                      <td>
                        {l.code} · {l.name}
                      </td>
                      <td>{l.nature}</td>
                      <td>{l.debit}</td>
                      <td>{l.credit}</td>
                      <td>{l.naturalBalance}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </>
      )}
    </section>
  );
}
